import _ from "lodash";

/**
 * Util for transforming json (usually from a server response)
 * to a proper javascript type, with basic error handling for unexpected types.
 * NOTE: the optional functions log to console.error and return undefined on errors
 * NOTE: the non-optional functions throw an error
 */

type Parent = any;

type Index = string;

function createError(msg: string, fieldname: Index, jsonParent: Parent) {
    return new Error(
        "Field " + fieldname + " has unexpected value: " + msg + ". Json container: " + JSON.stringify(jsonParent)
    );
}

function handleError(msg: string, fieldname: Index, jsonParent: Parent) {
    console.error(createError(msg, fieldname, jsonParent));
    return undefined;
}

export default function (jsonParent: Parent) {
    return {
        dateOptional: function (fieldname: Index): Date | undefined | null {
            const value = jsonParent[fieldname];
            if (typeof value === "number" || _.isString(value)) {
                return new Date(value);
            }
            if (value === undefined || value === null) {
                return value;
            }
            return handleError("expected a timestamp or undefined or null", fieldname, jsonParent);
        },

        directObject: function (): {
            [x: string]: any;
        } {
            const value = jsonParent;
            if (_.isObject(value) && !_.isArray(value)) {
                return value;
            }
            throw createError("expected an object", "<json root>", jsonParent);
        },

        object: function (fieldname: Index): {
            [x: string]: any;
        } {
            const value = jsonParent[fieldname];
            if (_.isObject(value) && !_.isArray(value)) {
                return value;
            }
            throw createError("expected an object", fieldname, jsonParent);
        },

        objectOptional: function (fieldname: Index): {
            [x: string]: any;
        } {
            const value = jsonParent[fieldname];
            if (_.isObject(value) && !_.isArray(value)) {
                return value;
            }
            if (value === undefined || value === null) {
                return value;
            }
            throw handleError("expected an object or undefined or null", fieldname, jsonParent);
        },

        arrayOptional: function (fieldname: Index): Array<any> | undefined | null {
            const value = jsonParent[fieldname];
            if (_.isArray(value)) {
                return value;
            }
            if (value === undefined || value === null) {
                return value;
            }
            return handleError("expected an array or undefined or null", fieldname, jsonParent);
        },

        directArray: function (): Array<any> {
            const value = jsonParent;
            if (_.isArray(value)) {
                return value;
            }
            throw createError("expected an array", "<json root>", jsonParent);
        },

        array: function (fieldname: Index): Array<any> {
            const value = jsonParent[fieldname];
            if (_.isArray(value)) {
                return value;
            }
            throw createError("expected an array", fieldname, jsonParent);
        },

        numberOptional: function (fieldname: Index): number | undefined | null {
            const value = jsonParent[fieldname];
            if (_.isNumber(value)) {
                return value;
            }
            if (value === undefined || value === null) {
                return value;
            }
            return handleError("expected a number or undefined or null", fieldname, jsonParent);
        },

        number: function (fieldname: Index): number {
            const value = jsonParent[fieldname];
            if (_.isNumber(value)) {
                return value;
            }
            throw createError("expected a number", fieldname, jsonParent);
        },

        booleanOptional: function (fieldname: Index): boolean | undefined | null {
            const value = jsonParent[fieldname];
            if (_.isBoolean(value)) {
                return value;
            }
            if (value === undefined || value === null) {
                return value;
            }
            return handleError("expected a boolean or undefined or null", fieldname, jsonParent);
        },

        stringOptional: function (fieldname: Index): string | undefined | null {
            const value = jsonParent[fieldname];
            if (_.isString(value)) {
                return value;
            }
            if (value === undefined || value === null) {
                return value;
            }
            return handleError("expected a string or undefined or null", fieldname, jsonParent);
        },

        string: function (fieldname: Index): string {
            const value = jsonParent[fieldname];
            if (_.isString(value)) {
                return value;
            }
            throw createError("expected a string", fieldname, jsonParent);
        }
    };
}
